import {
  DIRECTIONS,
  SURFACES,
  TIRE_COMPOUND_BY_ID,
  type Direction,
  type Surface,
} from "~/lib/tire-compounds";

// The tire page's view state as it travels in the URL, so a link opens the
// same chart. Every field is optional: a missing or invalid one means the
// page's default, and the page writes only the fields that differ from it.

export const Y_MODES = ["diff", "grip", "raw", "scale"] as const;
export type YMode = (typeof Y_MODES)[number];
export const Y_DEFAULT: YMode = "grip";
export const X_SCALES = ["sqrt", "linear"] as const;
export type XScale = (typeof X_SCALES)[number];

export const MAX_SELECTED = 8;
export const LOAD_MIN = 10;
export const LOAD_MAX = 1000;
export const LOAD_DEFAULT = 500;
export const LOAD_STEP = 10;
// Tire pressure in psi, the game's tuning range; 30 is a common stock setting.
export const PSI_MIN = 15;
export const PSI_MAX = 55;
export const PSI_DEFAULT = 30;
// Tire temperature in degrees Fahrenheit, the unit of the game's curves; 200 sits
// on the flat top of every road compound's curve.
export const TEMP_MIN = 32;
export const TEMP_MAX = 392;
export const TEMP_DEFAULT = 200;
// The compounds the page opens with, in legend order: Semi-Slick, Sport, Slick,
// Rally and Offroad, one from each group a tuner compares across.
// These are compound ids, so renumbering the set has to carry them along.
export const DEFAULT_SELECTION = [2, 6, 1, 18, 10];

export interface TireSearch {
  surface?: Surface;
  wet?: boolean;
  dir?: Direction;
  load?: number;
  // Tire pressure in psi.
  psi?: number;
  // Tire temperature in degrees Fahrenheit.
  temp?: number;
  y?: YMode;
  // Square vertical axis; the linear axis is the default.
  ysq?: boolean;
  x?: XScale;
  // Selected compound ids in legend order.
  tires?: number[];
  // Reference compound id, one of `tires`.
  ref?: number;
}

function oneOf<T extends string>(value: unknown, options: readonly T[]): T | undefined {
  return typeof value === "string" && (options as readonly string[]).includes(value)
    ? (value as T)
    : undefined;
}

function integer(value: unknown): number | undefined {
  const n =
    typeof value === "number"
      ? value
      : typeof value === "string" && value.trim() !== ""
        ? Number(value)
        : NaN;
  return Number.isInteger(n) ? n : undefined;
}

// The router parses each raw value as JSON first, so a lone id arrives as a
// number and a comma list as a string. It also validates more than once and
// then hands back this parser's own output, so an array must pass through.
function idList(value: unknown): number[] | undefined {
  const parts: unknown[] = Array.isArray(value)
    ? value
    : typeof value === "string"
      ? value.split(",")
      : typeof value === "number"
        ? [value]
        : [];
  const ids: number[] = [];
  for (const part of parts) {
    const id = integer(part);
    if (id !== undefined && TIRE_COMPOUND_BY_ID.has(id) && !ids.includes(id)) ids.push(id);
  }
  return ids.length > 0 ? ids.slice(0, MAX_SELECTED) : undefined;
}

export function parseTireSearch(search: Record<string, unknown>): TireSearch {
  const load = integer(search.load);
  const tires = idList(search.tires);
  const ref = integer(search.ref);
  const psi = integer(search.psi);
  const temp = integer(search.temp);
  return {
    surface: oneOf(search.surface, SURFACES),
    wet: search.wet === true || search.wet === 1 || search.wet === "1" ? true : undefined,
    dir: oneOf(search.dir, DIRECTIONS),
    load:
      load !== undefined && load >= LOAD_MIN && load <= LOAD_MAX
        ? Math.round(load / LOAD_STEP) * LOAD_STEP
        : undefined,
    psi: psi !== undefined && psi >= PSI_MIN && psi <= PSI_MAX ? psi : undefined,
    temp: temp !== undefined && temp >= TEMP_MIN && temp <= TEMP_MAX ? temp : undefined,
    y: oneOf(search.y, Y_MODES),
    ysq: search.ysq === true || search.ysq === 1 || search.ysq === "1" ? true : undefined,
    x: oneOf(search.x, X_SCALES),
    tires,
    ref: ref !== undefined && tires?.includes(ref) ? ref : undefined,
  };
}
